import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Bookmark, Clock, History, Trash2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ContinueCard } from "@/components/catalog/ContinueCard";
import { EmptyState, PageHeader, Spinner, TitleGrid } from "@/components/catalog/TitleGrid";
import { useProfiles } from "@/context/ProfileContext";
import { api } from "@/lib/api";
import { watchPath } from "@/lib/play";

const HistoryRow = ({ item, onRemove }) => {
  const { title, episode } = item;
  const pct = Math.min(100, Math.round(item.progress * 100));
  return (
    <div data-testid={`history-row-${title.slug}`} className="flex items-center gap-4 rounded-2xl border border-[#D4AF37]/15 bg-[#0B0B0D]/80 p-3 hover:border-[#D4AF37]/45 transition-colors">
      <Link to={`/title/${title.slug}`} className="flex-none"><img src={title.poster_url} alt="" className="h-24 w-16 rounded-lg object-cover border border-[#D4AF37]/25" /></Link>
      <div className="flex-1 min-w-0">
        <Link to={`/title/${title.slug}`} className="font-display text-base text-white hover:text-[#FFD86A] transition-colors">{title.title}</Link>
        <p className="text-xs text-[#8B8B8B] truncate">{episode ? `S${episode.season}:E${episode.number} · ${episode.title}` : title.type} · {formatDistanceToNow(new Date(item.updated_at), { addSuffix: true })}</p>
        <div className="mt-2 h-1 max-w-sm rounded-full bg-white/15"><div className="h-full rounded-full bg-gradient-to-r from-[#D4AF37] to-[#FFD86A]" style={{ width: `${pct}%` }} /></div>
        <p className="mt-1 text-[0.7rem] text-[#F5C451]">{item.completed ? "Completed" : `${pct}% watched`}</p>
      </div>
      <Link to={watchPath(title.slug, episode?.id)} data-testid={`history-play-${title.slug}`} className="btn-ghost-gold hidden sm:inline-flex px-4 py-2 text-sm">Play</Link>
      <button data-testid={`history-remove-${title.slug}`} onClick={() => onRemove(title.slug)} aria-label="Remove from history" className="grid h-10 w-10 place-items-center rounded-full text-[#8B8B8B] hover:text-[#E50914] hover:bg-[#E50914]/10 transition-colors">
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );
};

export default function Library() {
  const { activeProfile, myList } = useProfiles();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "list";
  const [data, setData] = useState(null);

  const load = () => Promise.all([api.get("/library/list"), api.get("/watch/continue"), api.get("/watch/history")])
    .then(([l, c, h]) => setData({ list: l.data, cont: c.data, history: h.data }))
    .catch(() => setData({ list: [], cont: [], history: [] }));
  useEffect(() => { load(); }, [activeProfile?.id, myList.size]); // eslint-disable-line react-hooks/exhaustive-deps

  const removeHistory = async (slug) => { await api.delete(`/watch/history/${slug}`).catch(() => {}); load(); };
  const browse = <Link to="/" data-testid="library-browse-btn" className="btn-gold px-6 py-3 text-sm">Browse titles</Link>;

  return (
    <div data-testid="library-page">
      <PageHeader eyebrow={`${activeProfile?.name}'s collection`} title="My Library" />
      <div className="mx-auto max-w-[1680px] px-4 sm:px-6 lg:px-10">
        <Tabs value={tab} onValueChange={(v) => setParams({ tab: v })}>
          <TabsList className="h-auto rounded-full border border-[#D4AF37]/25 bg-[#0B0B0D] p-1 mb-8">
            {[["list", "My List", Bookmark], ["continue", "Continue Watching", Clock], ["history", "History", History]].map(([v, label, Icon]) => (
              <TabsTrigger key={v} value={v} data-testid={`library-tab-${v}`} className="rounded-full px-4 sm:px-5 py-2 text-xs sm:text-sm font-semibold text-white/70 data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#F5C451] data-[state=active]:to-[#D4AF37] data-[state=active]:text-black">
                <Icon className="h-4 w-4 mr-1.5 hidden sm:inline" /> {label}
              </TabsTrigger>
            ))}
          </TabsList>
          {!data ? <Spinner /> : (
            <>
              <TabsContent value="list">
                {data.list.length ? <TitleGrid items={data.list} testId="my-list-grid" /> : <EmptyState icon={Bookmark} title="Your list is empty" text="Tap My List on any title to save it here." action={browse} testId="my-list-empty" />}
              </TabsContent>
              <TabsContent value="continue">
                {data.cont.length ? (
                  <div data-testid="continue-grid" className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">{data.cont.map((i) => <ContinueCard key={i.title.slug} item={i} fluid />)}</div>
                ) : <EmptyState icon={Clock} title="Nothing in progress" text="Start watching something and pick up right where you left off." action={browse} testId="continue-empty" />}
              </TabsContent>
              <TabsContent value="history">
                {data.history.length ? (
                  <div data-testid="history-list" className="grid gap-3 lg:grid-cols-2">{data.history.map((i) => <HistoryRow key={i.title.slug} item={i} onRemove={removeHistory} />)}</div>
                ) : <EmptyState icon={History} title="No watch history" text="Titles you watch on this profile will appear here." action={browse} testId="history-empty" />}
              </TabsContent>
            </>
          )}
        </Tabs>
      </div>
    </div>
  );
}
